import React, { useEffect, useMemo } from 'react';
import OptimizedImage from '../common/OptimizedImage';
import FadeInOnScroll from '../common/FadeInOnScroll';
import { preloadCriticalImages, generatePlaceholder } from '../../utils/imageOptimization';
import { COMPANY_INFO } from '../../constants';
import aboutus_mobile_title from '../../assets/images/about_title.png';
import mobileProfile from '../../assets/images/mobileprofile.png';

const AboutSection = () => {
  // Memoize placeholder to prevent recreation on each render
  const placeholder = useMemo(() => generatePlaceholder(), []);

  // Preload critical images on component mount
  useEffect(() => {
    preloadCriticalImages();
  }, []);

  const aboutContent = {
    intro: `At ${COMPANY_INFO.name}, we pride ourselves on a customer-centered approach that prioritizes your schedule and budget without compromising quality. With competitive pricing and efficient timelines, our skilled team ensures seamless project management from planning to execution.`,
    legacy: "Founded by Mr. Lenu George on May 2002, Maifloer brings over 25 years of experience in delivering excellence. Our journey has been marked by consistent growth and innovation. Today, Maifloer stands as a team of 50+ dedicated employees committed to providing top-notch services. Mr. George holds an MBA in Marketing Analysis, enabling us to set new benchmarks in the industry.",
    description: "Lenu George is a multifaceted individual with various professional and personal pursuits.",
    sections: [
      {
        title: "Business and Entrepreneurship:",
        items: [
          "Founder of Maifloer, a company delivering excellence in various services since 2002, with over 25 years of experience",
          "Maifloer Construction & Engineering LLP, working on turnkey projects, including construction, interiors, and electrical projects."
        ]
      },
      {
        title: "Education:",
        items: [
          "Holds an MBA in Marketing, which enables Maifloer to set new benchmarks in the industry."
        ]
      },
      {
        title: "Professional Expertise:",
        items: [
          "Expertise in delivering turnkey solutions, combining quality craftsmanship with innovative design",
          "Services include commercial and residential design, power solutions, home automation, and surveillance"
        ]
      }
    ]
  };

  return (
    <div
      id="about-section"
      className="relative w-full min-h-[100svh] overflow-hidden flex items-center md:items-center justify-end"
    >
      {/* Desktop Layout */}
      <FadeInOnScroll className="hidden md:flex relative z-20 w-full h-full items-center justify-center px-12">
        <div className="flex items-center justify-between w-full max-w-6xl">
          {/* Profile Image */}
          <div className="flex-shrink-0 mr-12 p-4 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 shadow-xl h-[55rem] w-[400px] flex items-end justify-center">
            <OptimizedImage 
              src={mobileProfile} 
              alt="Lenu George - Founder of Maifloer Technologies" 
              className="h-[27rem] w-[22rem] object-cover rounded-xl" 
              placeholderSrc={placeholder}
              priority={true}
            />
          </div>
          
          {/* Content */}
          <div className="flex-1 text-white">
            <OptimizedImage 
              src={aboutus_mobile_title} 
              alt="About Us - Maifloer Technologies" 
              className="mb-8 w-auto h-[60px]" 
              placeholderSrc={placeholder}
              priority={true}
            />
            
            <p className="mb-6 text-base leading-relaxed">{aboutContent.intro}</p>
            <p className="font-bold mb-4 text-lg">Our Legacy and Expertise</p>
            <p className="mb-6 text-base leading-relaxed">{aboutContent.legacy}</p>
            <p className="mb-4 text-base leading-relaxed">{aboutContent.description}</p>
            
            {aboutContent.sections.map((section, index) => (
              <div key={index} className="mb-4">
                <p className="font-bold mb-2 text-base">• {section.title}</p>
                {section.items.map((item, itemIndex) => (
                  <p key={itemIndex} className="mb-2 text-base leading-relaxed ml-4">{item}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      </FadeInOnScroll>

      {/* Mobile Layout */}
      <FadeInOnScroll className="block md:hidden relative z-20 w-full px-6">
        <div className="mb-6 flex justify-center w-full">
          <OptimizedImage 
            src={aboutus_mobile_title} 
            alt="About Us - Maifloer Technologies" 
            className="h-[3rem] w-auto" 
            placeholderSrc={placeholder}
            priority={true}
          />
        </div>
        
        <div className="mb-6 flex justify-center">
          <div className="p-3 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 shadow-lg w-[23rem] justify-center flex">
            <OptimizedImage 
              src={mobileProfile} 
              alt="Lenu George - Founder of Maifloer Technologies" 
              className="w-[200px] h-[250px] object-cover rounded-xl" 
              placeholderSrc={placeholder}
              priority={true}
            />
          </div>
        </div>
        
        <div className="text-white text-[12px] leading-relaxed">
          <p className="mb-6">{aboutContent.intro}</p>
          {/* <p className="font-bold mb-4">Our Legacy and Expertise</p> */}
          <p className="mb-6">{aboutContent.legacy}</p>
          <p className="mb-4">{aboutContent.description}</p>
          
          {aboutContent.sections.map((section, index) => (
            <div key={index} className="mb-4">
              <p className="font-bold mb-2">• {section.title}</p>
              {section.items.map((item, itemIndex) => (
                <p key={itemIndex} className="mb-2 ml-4">{item}</p>
              ))}
            </div>
          ))}
        </div>
      </FadeInOnScroll>
    </div>
  );
};

export default AboutSection;